// components/FullAnalysisPage.jsx - 股票详细分析页面 (重构版 v3.0)
import React, { useEffect, useState, useMemo } from "react";
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, ComposedChart,
  Legend, Line, LineChart, ReferenceLine, ResponsiveContainer,
  Tooltip, XAxis, YAxis
} from "recharts";
import { theme } from '../theme';
import { SignalBadge, VolumeBadge, SentimentBadge, ChangeIndicator, CustomTooltip } from './UIComponents';
import { useAuth } from '../AuthContext';
import { addToWatchlist, removeFromWatchlist, isInWatchlist, updateWatchlistRiskSnapshot, savePrediction, getTodayPrediction, getPredictionHistory, saveMarketSenseData, getMarketSenseData, settlePredictions, saveTodayPick, getTodayPick, recordLoginDay, getPickAllowance, getTrialInfo } from '../firebase';
import { StockKlineChart } from './charts/StockKlineChart';
import { DailyPrediction } from './prediction/DailyPrediction';
import { MarketSenseIQ } from './prediction/MarketSenseIQ';
import { SettlementModal } from './prediction/SettlementModal';

// Share Prediction Component
function SharePrediction({ ticker, userTier, aiTier }) {
  const [copied, setCopied] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const tierLabel = (t) => ({ big_rise: 'Strong Rise', small_rise: 'Small Rise', flat: 'Flat', small_drop: 'Small Drop', big_drop: 'Strong Drop' }[t] || t);
  const siteUrl = window.location.origin;
  const shareText = `I predicted $${ticker} will see a ${tierLabel(userTier)} tomorrow! AI says ${tierLabel(aiTier)}. Who will be right? Try it free on InsightStock`;
  const btnSmall = { padding: '8px 14px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 600 };
  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button onClick={() => setShowMenu(!showMenu)} style={{ ...btnSmall, background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.5)', border: '1px solid rgba(255,255,255,0.12)', fontSize: '13px', padding: '8px 16px' }}>
        Share
      </button>
      {showMenu && (
        <div style={{ position: 'absolute', bottom: '100%', right: 0, marginBottom: '8px', background: theme.bgCard, border: '1px solid ' + theme.border, borderRadius: '12px', padding: '10px', display: 'flex', gap: '6px', boxShadow: '0 8px 30px rgba(0,0,0,0.3)', zIndex: 100 }}>
          <button onClick={() => window.open('https://twitter.com/intent/tweet?text=' + encodeURIComponent(shareText + ' ' + siteUrl), '_blank')} style={{ ...btnSmall, background: '#1DA1F2', color: '#fff' }}>X</button>
          <button onClick={() => window.open('https://www.linkedin.com/sharing/share-offsite/?url=' + encodeURIComponent(siteUrl), '_blank')} style={{ ...btnSmall, background: '#0A66C2', color: '#fff' }}>LinkedIn</button>
          <button onClick={() => window.open('https://wa.me/?text=' + encodeURIComponent(shareText + ' ' + siteUrl), '_blank')} style={{ ...btnSmall, background: '#25D366', color: '#fff' }}>WhatsApp</button>
          <button onClick={() => { navigator.clipboard.writeText(shareText + ' ' + siteUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); }} style={{ ...btnSmall, background: 'rgba(255,255,255,0.1)', color: '#fff', border: '1px solid rgba(255,255,255,0.15)' }}>{copied ? 'Copied!' : 'Copy'}</button>
        </div>
      )}
    </div>
  );
}

export function FullAnalysisPage({ ticker, onBack, onPortfolio, onFeedback, onMarketSense, onPricing }) {
  const { user, userData: fullUserData } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [streamText, setStreamText] = useState('');
  const [error, setError] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [currentTicker, setCurrentTicker] = useState(ticker);

  // Watchlist 状态
  const [inWatchlist, setInWatchlist] = useState(false);
  const [watchlistLoading, setWatchlistLoading] = useState(false);

  // Market Context 数据（分开加载）
  const [marketContextData, setMarketContextData] = useState(null);
  const [marketContextAnalysis, setMarketContextAnalysis] = useState(null);
  const [marketContextLoading, setMarketContextLoading] = useState(false);

  // Prediction & MarketSenseIQ states
  const [userPrediction, setUserPrediction] = useState(null);   // tier id after submit
  const [userMood, setUserMood] = useState(null);
  const [aiPrediction, setAiPrediction] = useState(null);       // computed from technical
  const [aiConfidence, setAiConfidence] = useState(0);
  const [aiStrength, setAiStrength] = useState('Weak');
  const [alreadyPredicted, setAlreadyPredicted] = useState(false);
  const [iqScore, setIqScore] = useState(150);
  const [savedPicks, setSavedPicks] = useState(null); // array of picks
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [moodStats, setMoodStats] = useState({
    calm: { total: 0, correct: 0 },
    confident: { total: 0, correct: 0 },
    anxious: { total: 0, correct: 0 },
    impulsive: { total: 0, correct: 0 },
  });
  const [predictionHistory, setPredictionHistory] = useState([]);
  const [showSettlement, setShowSettlement] = useState(false);
  const [settlementData, setSettlementData] = useState(null);

  // Market indices and collapsible sections
  const [marketIndices, setMarketIndices] = useState(null);
  const [showTechnical, setShowTechnical] = useState(false);
  const [showSentiment, setShowSentiment] = useState(false);
  const [showCharts, setShowCharts] = useState(false);
  const [showAiAnalysis, setShowAiAnalysis] = useState(false);

  const API_BASE = 'https://insightstock-api-358482645476.us-west2.run.app';

  // 检查是否在 watchlist
  useEffect(() => {
    if (user && currentTicker) {
      isInWatchlist(user.uid, currentTicker).then(result => setInWatchlist(result));
    }
  }, [user, currentTicker]);

  // Watchlist 切换
  const toggleWatchlist = async () => {
    if (!user) return;
    setWatchlistLoading(true);
    try {
      if (inWatchlist) {
        await removeFromWatchlist(user.uid, currentTicker);
        setInWatchlist(false);
      } else {
        const riskClassification = data?.analysis?.riskClassification || {};
        const result = await addToWatchlist(
          user.uid,
          currentTicker,
          data?.company?.name || currentTicker,
          data?.stockPrice?.latestClose,
          riskClassification
        );
        if (result.success) setInWatchlist(true);
      }
    } catch (err) { console.error('Watchlist error:', err); }
    finally { setWatchlistLoading(false); }
  };

  // 获取 Market Context 数据（秒出）
  const fetchMarketContextData = async (t) => {
    try {
      const res = await fetch(API_BASE + '/api/stock/' + t + '/market-context/data');
      const result = await res.json();
      if (result.success && result.data) {
        setMarketContextData(result.data);
      }
    } catch (err) {
      console.error('Failed to fetch market context data:', err);
    }
  };

  // 获取 Market Context 分析（慢，OpenAI）
  const fetchMarketContextAnalysis = async (t) => {
    try {
      setMarketContextLoading(true);
      const res = await fetch(API_BASE + '/api/stock/' + t + '/market-context');
      const result = await res.json();
      if (result.success && result.analysis) {
        setMarketContextAnalysis(result.analysis);
      }
    } catch (err) {
      console.error('Failed to fetch market context analysis:', err);
    } finally {
      setMarketContextLoading(false);
    }
  };

  // 获取 AI 分析 (Stream 版本)
  const fetchAnalysisStream = async (t) => {
    try {
      setAnalysisLoading(true);
      setStreamText('');

      const response = await fetch(API_BASE + '/api/stock/' + t + '/analysis/stream');
      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      let fullText = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const jsonStr = line.slice(6);
              const data = JSON.parse(jsonStr);

              if (data.chunk) {
                fullText += data.chunk;
                setStreamText(fullText);
              }

              if (data.done && data.analysis) {
                setData(prev => prev ? {
                  ...prev,
                  analysis: data.analysis,
                  judgeSummary: data.judgeSummary
                } : prev);
                setStreamText('');
              }

              if (data.cached && data.analysis) {
                setData(prev => prev ? {
                  ...prev,
                  analysis: data.analysis,
                  judgeSummary: data.judgeSummary
                } : prev);
                setStreamText('');
              }

              if (data.error) {
                console.error('Stream error:', data.error);
              }
            } catch (e) {
              // 解析失败，忽略
            }
          }
        }
      }
    } catch (err) {
      console.error('Failed to fetch analysis:', err);
      try {
        const res = await fetch(API_BASE + '/api/stock/' + t + '/analysis');
        const result = await res.json();
        if (result.analysis) {
          setData(prev => prev ? {
            ...prev,
            analysis: result.analysis,
            judgeSummary: result.judgeSummary
          } : prev);
        }
      } catch (e) {
        console.error('Fallback also failed:', e);
      }
    } finally {
      setAnalysisLoading(false);
    }
  };

  const loadStock = (t) => {
    setLoading(true);
    setError(null);
    setCurrentTicker(t.toUpperCase());
    setData(null);
    setStreamText('');
    setInWatchlist(false);
    setMarketContextData(null);
    setMarketContextAnalysis(null);
    resetPredictionStates();

    // 并行加载：主数据 + Market Context 数据（秒出）
    fetch(API_BASE + '/api/stock/' + t)
      .then(res => {
        if (res.status === 429) throw new Error('Daily query limit reached.');
        if (!res.ok) throw new Error('Symbol not found');
        return res.json();
      })
      .then(json => {
        if (!json.stockPrice || json.stockPrice.latestClose === null) {
          setError('Symbol "' + t.toUpperCase() + '" not found.');
          setLoading(false);
          return;
        }
        setData(json);
        setLoading(false);

        // Compute AI prediction immediately from technical data
        if (json.technical) {
          const aiResult = computeAiPrediction(json.technical);
          setAiPrediction(aiResult.tier);
          setAiConfidence(aiResult.confidence);
          setAiStrength(aiResult.strength);
        }

        // 检查是否在 watchlist + 结算 + 加载今天的预测
        if (user) {
          isInWatchlist(user.uid, t.toUpperCase()).then(result => setInWatchlist(result));

          // Settle pending predictions from previous days (runs in background)
          settlePredictions(user.uid).then(res => {
            if (res.settled > 0) console.log(`Settled ${res.settled} predictions`);
          });

          // Load today's prediction from Firebase
          getTodayPrediction(user.uid, t.toUpperCase()).then(pred => {
            if (pred) {
              setUserPrediction(pred.tier);
              setUserMood(pred.mood);
              setAiPrediction(pred.aiTier);
              setAlreadyPredicted(true);
              // Add to local history display
              const d = new Date(pred.date);
              const dateStr = (d.getMonth() + 1) + '/' + d.getDate();
              setPredictionHistory(prev => {
                if (prev.some(p => p.date === dateStr)) return prev;
                return [{ date: dateStr, userTier: pred.tier, aiTier: pred.aiTier, actualChange: pred.actualChange, userCorrect: pred.userCorrect, aiCorrect: pred.aiCorrect, mood: pred.mood, iqChange: pred.iqChange || 0 }, ...prev];
              });
            }
          });

          // Load Market Sense IQ data
          getMarketSenseData(user.uid).then(msData => {
            if (msData) {
              setIqScore(msData.iqScore || 150);
              setStreak(msData.streak || 0);
              setBestStreak(msData.bestStreak || 0);
              if (msData.moodStats) setMoodStats(msData.moodStats);
            }
          });

          // Load today's saved picks (array)
          getTodayPick(user.uid).then(picks => {
            if (picks && picks.length > 0) setSavedPicks(picks);
          });

          // Load prediction history
          getPredictionHistory(user.uid, t.toUpperCase()).then(history => {
            if (history && history.length > 0) {
              const formatted = history.map(h => {
                const d = new Date(h.date);
                return {
                  date: (d.getMonth() + 1) + '/' + d.getDate(),
                  userTier: h.tier,
                  aiTier: h.aiTier,
                  actualChange: h.actualChange,
                  userCorrect: h.userCorrect,
                  aiCorrect: h.aiCorrect,
                  mood: h.mood,
                  iqChange: h.iqChange || 0,
                };
              });
              setPredictionHistory(formatted);
            }
          });
        }

        // 异步获取 AI 分析
        if (!json.analysis) {
          fetchAnalysisStream(t.toUpperCase());
        }
      })
      .catch((err) => {
        setError(err.message || 'Failed to load');
        setLoading(false);
      });

    // 同时加载 Market Context（秒出 + 慢的分开）
    fetchMarketContextData(t.toUpperCase());
    fetchMarketContextAnalysis(t.toUpperCase());

    // Load market indices for alert bar
    fetch(API_BASE + '/api/market/indices')
      .then(res => res.json())
      .then(d => { if (d.success && d.data) setMarketIndices(d.data); })
      .catch(() => {});
  };

  // Generate mock OHLCV price history when backend doesn't return it
  // (temporary fallback until backend is deployed with priceHistory field)
  const generateMockPriceHistory = (basePrice) => {
    const price = basePrice || 150;
    const result = [];
    const now = new Date();
    let p = price * 0.75; // start ~25% below current
    for (let i = 120; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      if (d.getDay() === 0 || d.getDay() === 6) continue; // skip weekends
      const change = (Math.random() - 0.48) * price * 0.03;
      const open = p;
      const close = p + change;
      const high = Math.max(open, close) + Math.random() * price * 0.01;
      const low = Math.min(open, close) - Math.random() * price * 0.01;
      const volume = Math.floor(20000000 + Math.random() * 40000000);
      result.push({
        date: d.getTime(),
        open: +open.toFixed(2),
        high: +high.toFixed(2),
        low: +low.toFixed(2),
        close: +close.toFixed(2),
        volume,
      });
      p = close;
    }
    return result;
  };

  // Compute AI prediction from technical indicators
  const computeAiPrediction = (techData) => {
    if (!techData) return { tier: 'flat', confidence: 45, strength: 'Weak' };
    const { overall, volumeSignal, maSignal, rsiSignal } = techData;

    let tier = 'flat';
    if (overall === 'bullish' && volumeSignal === 'surge') tier = 'big_rise';
    else if (overall === 'bullish') tier = 'small_rise';
    else if (overall === 'bearish' && volumeSignal === 'surge') tier = 'big_drop';
    else if (overall === 'bearish') tier = 'small_drop';

    // Confidence: how many signals agree
    let agree = 0;
    const dir = tier.includes('rise') ? 'bullish' : tier.includes('drop') ? 'bearish' : 'neutral';
    if (overall === dir || (dir === 'neutral' && overall === 'neutral')) agree++;
    if ((dir === 'bullish' && volumeSignal === 'surge') || (dir === 'bearish' && volumeSignal === 'surge') || (dir === 'neutral' && volumeSignal !== 'surge')) agree++;
    if ((dir === 'bullish' && maSignal === 'bullish') || (dir === 'bearish' && maSignal === 'bearish') || (dir === 'neutral')) agree++;
    if ((dir === 'bullish' && rsiSignal !== 'overbought') || (dir === 'bearish' && rsiSignal !== 'oversold') || (dir === 'neutral')) agree++;

    const confidence = Math.min(95, Math.max(35, 40 + agree * 14));
    const strength = confidence >= 75 ? 'Strong' : confidence >= 55 ? 'Medium' : 'Weak';

    return { tier, confidence, strength };
  };

  // Handle prediction submission
  const handlePredictionSubmit = (predictionData) => {
    const { tier, mood } = predictionData;
    setUserPrediction(tier);
    setUserMood(mood);
    setAlreadyPredicted(true);

    // Compute AI prediction at submission time
    const aiResult = computeAiPrediction(data?.technical);
    const aiTier = aiResult.tier;
    setAiPrediction(aiTier);
    setAiConfidence(aiResult.confidence);
    setAiStrength(aiResult.strength);

    // Immediately add to prediction history (actual result TBD)
    const today = new Date();
    const dateStr = (today.getMonth() + 1) + '/' + today.getDate();
    const newEntry = {
      date: dateStr,
      userTier: tier,
      aiTier: aiTier,
      actualChange: null,     // unknown until next day
      userCorrect: null,
      aiCorrect: null,
      mood: mood,
      iqChange: 0,
    };
    setPredictionHistory(prev => [newEntry, ...prev]);

    // Update mood stats
    setMoodStats(prev => {
      const moodKey = mood; // 'calm', 'confident', 'anxious', 'impulsive'
      const current = prev[moodKey] || { total: 0, correct: 0 };
      return {
        ...prev,
        [moodKey]: { ...current, total: current.total + 1 },
      };
    });

    // Save to Firebase for persistence
    if (user) {
      savePrediction(user.uid, currentTicker, { tier, mood, aiTier }).then(() => {
        console.log('Prediction saved to Firebase');
      }).catch(err => console.error('Firebase save error:', err));

      // Also save updated mood stats
      saveMarketSenseData(user.uid, {
        iqScore,
        streak,
        bestStreak,
        moodStats: {
          ...moodStats,
          [mood]: { ...(moodStats[mood] || { total: 0, correct: 0 }), total: (moodStats[mood]?.total || 0) + 1 },
        },
      });
    }
  };

  // Reset prediction states when ticker changes
  const resetPredictionStates = () => {
    setUserPrediction(null);
    setUserMood(null);
    setAiPrediction(null);
    setAlreadyPredicted(false);
    setPredictionHistory([]);
    setSavedPicks(null);
  };

  // Memoize K-line data so it doesn't regenerate on every render
  const klineData = useMemo(() => {
    if (data?.priceHistory && data.priceHistory.length > 0) {
      return data.priceHistory;
    }
    return generateMockPriceHistory(data?.stockPrice?.latestClose);
  }, [data?.priceHistory, data?.stockPrice?.latestClose]);

  // Compute Momentum + RSI client-side from price history (works even if backend hasn't deployed yet)
  const clientIndicators = useMemo(() => {
    if (!klineData || klineData.length < 15) return { momentum: null, rsi: null, rsiSignal: null };
    const closes = klineData.map(p => p.close);
    // Momentum (14-day ROC)
    const current = closes[closes.length - 1];
    const past = closes[closes.length - 15];
    const momentum = past > 0 ? Math.round(((current - past) / past) * 10000) / 100 : null;
    // RSI (14-day)
    let gains = 0, losses = 0;
    for (let i = closes.length - 14; i < closes.length; i++) {
      const change = closes[i] - closes[i - 1];
      if (change > 0) gains += change; else losses += Math.abs(change);
    }
    const avgGain = gains / 14, avgLoss = losses / 14;
    const rsi = avgLoss === 0 ? 100 : Math.round((100 - 100 / (1 + avgGain / avgLoss)) * 10) / 10;
    const rsiSignal = rsi >= 70 ? 'overbought' : rsi <= 30 ? 'oversold' : 'neutral';
    return { momentum, rsi, rsiSignal };
  }, [klineData]);

  useEffect(() => { if (user) loadStock(ticker); }, [ticker, user]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchInput.trim()) loadStock(searchInput.trim());
  };

  // 通用模块样式
  const sectionStyle = {
    background: theme.bgCard,
    border: '1px solid ' + theme.border,
    borderRadius: '14px',
    padding: '32px',
    marginBottom: '28px'
  };

  const sectionTitleStyle = {
    color: theme.text,
    fontSize: '28px',
    fontWeight: 700,
    margin: '0 0 8px',
    textTransform: 'uppercase',
    letterSpacing: '1px'
  };

  const sectionSubtitleStyle = {
    color: theme.textDark,
    fontSize: '18px',
    fontStyle: 'italic',
    margin: '0 0 24px'
  };

  const contentBoxStyle = {
    background: theme.bg,
    borderRadius: '12px',
    padding: '24px',
    borderLeft: '4px solid ' + theme.teal
  };

  // 未登录用户，显示提示
  if (!user) {
    return (
      <div style={{ minHeight: '100vh', background: theme.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: theme.text, fontSize: '24px', marginBottom: '20px' }}>Please sign in to view full analysis</p>
        <button onClick={onBack} style={{ background: theme.teal, border: 'none', borderRadius: '10px', padding: '16px 32px', color: '#0f172a', fontSize: '18px', fontWeight: 700, cursor: 'pointer' }}>← Back to Home</button>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: theme.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: '60px', height: '60px', border: '3px solid ' + theme.border, borderTop: '3px solid ' + theme.teal, borderRadius: '50%', animation: 'spin 1s linear infinite', marginBottom: '28px' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <p style={{ color: theme.text, fontSize: '28px', margin: '0 0 12px' }}>Analyzing {currentTicker}</p>
        <p style={{ color: theme.textDark, fontSize: '20px' }}>AI analysis may take 15-30 seconds...</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: theme.bg, fontFamily: '-apple-system, sans-serif', color: theme.text, padding: '28px' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header: Nav Tabs + Search */}
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px', paddingBottom: '16px', borderBottom: '1px solid ' + theme.border, gap: '8px' }}>
          <button onClick={onBack} style={{ background: 'transparent', border: 'none', padding: '10px 16px', borderRadius: '8px', color: theme.textDark, fontSize: '18px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
            onMouseEnter={e => e.currentTarget.style.color = theme.teal} onMouseLeave={e => e.currentTarget.style.color = theme.textDark}>← Home</button>
          {[
            { label: 'Full Analysis', active: true },
            { label: 'Market Sense', onClick: onMarketSense },
            { label: 'Portfolio', onClick: onPortfolio },
            { label: 'Feedback', onClick: onFeedback, muted: true },
          ].map((item, i) => (
            <button key={i} onClick={item.onClick || undefined} style={{
              background: item.active ? 'rgba(45, 212, 191, 0.1)' : 'transparent',
              border: item.active ? '1px solid rgba(45, 212, 191, 0.3)' : 'none',
              padding: '10px 16px', borderRadius: '8px',
              color: item.active ? theme.teal : item.muted ? theme.textDark : theme.textMuted,
              fontSize: '18px', fontWeight: 600, cursor: item.active ? 'default' : 'pointer',
              transition: 'all 0.15s ease', whiteSpace: 'nowrap',
            }} onMouseEnter={e => { if (!item.active) { e.currentTarget.style.background = 'rgba(45, 212, 191, 0.08)'; e.currentTarget.style.color = theme.teal; }}}
               onMouseLeave={e => { if (!item.active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = item.muted ? theme.textDark : theme.textMuted; }}}>
              {item.label}
            </button>
          ))}
          <div style={{ flex: 1 }} />
          <form onSubmit={handleSearch} style={{ display: 'flex', gap: '8px' }}>
            <input type="text" value={searchInput} onChange={(e) => setSearchInput(e.target.value.toUpperCase())} placeholder="Symbol..." style={{ background: theme.bgCard, border: '1px solid ' + theme.border, borderRadius: '8px', padding: '12px 18px', color: theme.text, fontSize: '18px', width: '160px', outline: 'none' }} />
            <button type="submit" style={{ background: theme.teal, border: 'none', borderRadius: '8px', padding: '12px 28px', color: '#0f172a', fontSize: '18px', fontWeight: 700, cursor: 'pointer' }}>Go</button>
          </form>
        </div>

        {/* Error */}
        {error && (
          <div style={{ background: 'rgba(248, 81, 73, 0.1)', border: '1px solid ' + theme.red, borderRadius: '12px', padding: '24px', marginBottom: '24px', textAlign: 'center' }}>
            <p style={{ color: theme.red, fontSize: '22px', margin: 0, fontWeight: 600 }}>{error}</p>
          </div>
        )}

        {data && (
          <>
            {/* Market Alert Bar - NEW */}
            {marketIndices && (() => {
              // Backend returns {SPY: {name, ticker, dailyChange, ...}, QQQ: {...}, ...}
              const indicesArr = Object.values(marketIndices);
              const majorIndices = indicesArr.filter(idx => ['SPY', 'QQQ'].includes(idx.ticker));
              let marketHealth = 'stable';
              let alertColor = theme.green;
              let alertText = 'Market: Stable';

              if (majorIndices.some(idx => (idx.dailyChange || 0) < -2)) {
                marketHealth = 'danger';
                alertColor = theme.red;
                alertText = 'Market Alert: Major indices down >2%';
              } else if (majorIndices.some(idx => (idx.dailyChange || 0) < -0.5)) {
                marketHealth = 'caution';
                alertColor = theme.neutral;
                alertText = 'Market Caution: Indices showing weakness';
              }

              const pulseAnimation = marketHealth === 'danger' ? 'pulse 2s infinite' : 'none';

              return (
                <div style={{
                  background: alertColor + '15',
                  border: '2px solid ' + alertColor,
                  borderRadius: '12px',
                  padding: '18px 28px',
                  marginBottom: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  animation: pulseAnimation,
                  flexWrap: 'wrap',
                }}>
                  <span style={{ fontSize: '26px', lineHeight: 1 }}>
                    {marketHealth === 'danger' ? '🚨' : marketHealth === 'caution' ? '⚠️' : '✅'}
                  </span>
                  <span style={{ color: alertColor, fontWeight: 700, fontSize: '22px' }}>
                    {alertText}
                  </span>
                  <div style={{ flex: 1 }} />
                  {indicesArr.filter(idx => ['SPY', 'QQQ', 'DIA'].includes(idx.ticker)).map(idx => (
                    <div key={idx.ticker} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ color: theme.textDark, fontSize: '17px', fontWeight: 600 }}>{idx.name}</span>
                      <span style={{ color: (idx.dailyChange || 0) >= 0 ? theme.green : theme.red, fontSize: '19px', fontWeight: 700 }}>
                        {(idx.dailyChange || 0) >= 0 ? '+' : ''}{idx.dailyChange || 0}%
                      </span>
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* Quick Glance Card - Stock name/price + Risk + metrics row + bullets + CTA */}
            <div style={{
              background: theme.bgCard,
              border: '1px solid ' + theme.border,
              borderRadius: '16px',
              padding: '32px',
              marginBottom: '28px',
            }}>
              {/* Header: Name + Watchlist + Price */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <h1 style={{ fontFamily: 'Georgia, serif', fontSize: '42px', fontWeight: 400, color: theme.text, margin: 0 }}>
                      {data.company?.name || data.ticker}
                    </h1>
                    <button onClick={toggleWatchlist} disabled={watchlistLoading} style={{
                      background: inWatchlist ? 'rgba(250, 204, 21, 0.2)' : 'transparent',
                      border: inWatchlist ? '2px solid #facc15' : '2px solid ' + theme.border,
                      borderRadius: '10px', padding: '8px 16px', cursor: watchlistLoading ? 'wait' : 'pointer',
                      display: 'flex', alignItems: 'center', gap: '8px', fontSize: '18px',
                      transition: 'all 0.15s ease',
                    }}>
                      <span style={{ fontSize: '22px', color: inWatchlist ? '#facc15' : theme.textDark }}>{inWatchlist ? '★' : '☆'}</span>
                      <span style={{ color: inWatchlist ? '#facc15' : theme.textDark, fontWeight: 600 }}>{inWatchlist ? 'Watching' : 'Watch'}</span>
                    </button>
                    <span style={{ color: theme.textDark, fontSize: '13px', fontStyle: 'italic', alignSelf: 'center' }}>
                      {inWatchlist ? "We'll notify you when risk level changes" : 'Add to get notified when risk changes'}
                    </span>
                  </div>
                  <p style={{ color: theme.textMuted, fontSize: '18px', margin: '6px 0 0' }}>
                    {data.ticker} · {data.company?.industry}
                  </p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  {data.stockPrice && (
                    <>
                      <p style={{ fontSize: '48px', fontWeight: 700, color: theme.text, margin: 0 }}>
                        ${data.stockPrice.latestClose}
                      </p>
                      <p style={{
                        fontSize: '22px', fontWeight: 600,
                        color: (data.stockPrice.dailyChange || 0) >= 0 ? theme.green : theme.red,
                        margin: '4px 0 0'
                      }}>
                        {(data.stockPrice.dailyChange || 0) >= 0 ? '+' : ''}{data.stockPrice.dailyChange || 0}% (1D)
                      </p>
                    </>
                  )}
                </div>
              </div>

              {/* Dynamic Risk Strip — robot icon */}
              {data.analysis?.riskClassification && (() => {
                const rl = (data.analysis.riskClassification.riskLevel || '').toLowerCase();
                const riskColors = {
                  'low': theme.green,
                  'medium': theme.neutral,
                  'medium-high': '#ff9900',
                  'elevated': '#ff9900',
                  'high': theme.red,
                  'extreme': '#8b0000'
                };

                let riskColor = theme.neutral;
                for (const [key, color] of Object.entries(riskColors)) {
                  if (rl.includes(key)) {
                    riskColor = color;
                    break;
                  }
                }

                // Pick robot image based on risk level (R1=high, R2=elevated, R3=medium, R4=low)
                let robotSrc = '/R3.png';
                if (rl.includes('high') || rl.includes('extreme')) robotSrc = '/R1.png';
                else if (rl.includes('elevated')) robotSrc = '/R2.png';
                else if (rl.includes('medium')) robotSrc = '/R3.png';
                else if (rl.includes('low')) robotSrc = '/R4.png';

                const riskLevel = data.analysis.riskClassification.riskLevel;
                const basis = data.analysis.riskClassification.basis;

                return (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    marginBottom: '20px',
                  }}>
                    <img
                      src={robotSrc}
                      onError={(e) => { e.target.style.display = 'none'; }}
                      alt={riskLevel}
                      style={{ width: 160, height: 160, objectFit: 'contain', flexShrink: 0 }}
                    />
                    <div style={{ flex: 1 }}>
                      <span style={{ color: riskColor, fontWeight: 700, fontSize: '24px', display: 'block', marginBottom: '6px' }}>
                        {riskLevel}
                      </span>
                      <span style={{ color: theme.textDark, fontSize: '17px', lineHeight: '1.5' }}>
                        {basis}
                      </span>
                    </div>
                  </div>
                );
              })()}

              {/* Metrics Row - 5 columns */}
              {data.stockPrice && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px', marginBottom: '20px' }}>
                  <div style={{ background: theme.bg, borderRadius: '10px', padding: '16px', textAlign: 'center' }}>
                    <p style={{ color: theme.textDark, fontSize: '15px', margin: '0 0 6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>30D</p>
                    <p style={{ fontSize: '24px', fontWeight: 700, color: (data.stockPrice.change30Day || 0) >= 0 ? theme.green : theme.red, margin: 0 }}>
                      {(data.stockPrice.change30Day || 0) >= 0 ? '+' : ''}{data.stockPrice.change30Day || 0}%
                    </p>
                  </div>
                  <div style={{ background: theme.bg, borderRadius: '10px', padding: '16px', textAlign: 'center' }}>
                    <p style={{ color: theme.textDark, fontSize: '15px', margin: '0 0 6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Range</p>
                    <p style={{ fontSize: '19px', fontWeight: 600, color: theme.text, margin: 0 }}>
                      ${data.stockPrice.low30Day} - ${data.stockPrice.high30Day}
                    </p>
                  </div>
                  <div style={{ background: theme.bg, borderRadius: '10px', padding: '16px', textAlign: 'center' }}>
                    <p style={{ color: theme.textDark, fontSize: '15px', margin: '0 0 6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>YTD</p>
                    <p style={{ fontSize: '24px', fontWeight: 700, color: (data.stockPrice.ytdReturn || 0) >= 0 ? theme.green : theme.red, margin: 0 }}>
                      {data.stockPrice.ytdReturn !== null ? ((data.stockPrice.ytdReturn >= 0 ? '+' : '') + data.stockPrice.ytdReturn + '%') : 'N/A'}
                    </p>
                  </div>
                  <div style={{ background: theme.bg, borderRadius: '10px', padding: '16px', textAlign: 'center' }}>
                    <p style={{ color: theme.textDark, fontSize: '15px', margin: '0 0 6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>1Y</p>
                    <p style={{ fontSize: '24px', fontWeight: 700, color: (data.stockPrice.return1Y || 0) >= 0 ? theme.green : theme.red, margin: 0 }}>
                      {data.stockPrice.return1Y !== null ? ((data.stockPrice.return1Y >= 0 ? '+' : '') + data.stockPrice.return1Y + '%') : 'N/A'}
                    </p>
                  </div>
                  <div style={{ background: theme.bg, borderRadius: '10px', padding: '16px', textAlign: 'center' }}>
                    <p style={{ color: theme.textDark, fontSize: '15px', margin: '0 0 6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>MCap</p>
                    <p style={{ fontSize: '20px', fontWeight: 600, color: theme.text, margin: 0 }}>
                      {data.company?.market_cap ? (data.company.market_cap >= 1e12 ? '$' + (data.company.market_cap / 1e12).toFixed(2) + 'T' : '$' + (data.company.market_cap / 1e9).toFixed(1) + 'B') : 'N/A'}
                    </p>
                  </div>
                </div>
              )}

              {/* AI Analysis loading hint */}
              {!data.analysis && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', padding: '16px 20px', background: 'rgba(45, 212, 191, 0.06)', borderRadius: '10px' }}>
                  <div style={{ width: '20px', height: '20px', border: '2px solid ' + theme.border, borderTop: '2px solid ' + theme.teal, borderRadius: '50%', animation: 'spin 1s linear infinite', flexShrink: 0 }} />
                  <p style={{ color: theme.textMuted, fontSize: '16px', margin: 0 }}>AI is analyzing {currentTicker}... This may take a few seconds.</p>
                </div>
              )}

              {/* 3 Key Bullets */}
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {data.analysis?.opportunities?.[0] && (
                  <li style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', color: theme.text, fontSize: '18px', lineHeight: '1.5' }}>
                    <span style={{ color: theme.teal, fontWeight: 700, marginTop: '3px', fontSize: '18px' }}>▸</span>
                    <span>{data.analysis.opportunities[0]}</span>
                  </li>
                )}
                {data.analysis?.risks?.[0] && (
                  <li style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', color: theme.text, fontSize: '18px', lineHeight: '1.5' }}>
                    <span style={{ color: theme.red, fontWeight: 700, marginTop: '3px', fontSize: '18px' }}>▸</span>
                    <span>{data.analysis.risks[0]}</span>
                  </li>
                )}
                {data.analysis?.milestones?.[0] && (
                  <li style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', color: theme.text, fontSize: '18px', lineHeight: '1.5' }}>
                    <span style={{ color: theme.purple, fontWeight: 700, marginTop: '3px', fontSize: '18px' }}>▸</span>
                    <span>{data.analysis.milestones[0]}</span>
                  </li>
                )}
              </ul>
            </div>

            {/* AI vs You Prediction Zone - NEW LAYOUT */}
            <div style={{
              background: theme.bgCard,
              border: '1px solid ' + theme.border,
              borderRadius: '16px',
              padding: '32px',
              marginBottom: '28px',
            }}>
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <h2 style={{ color: theme.teal, fontSize: '32px', fontWeight: 800, margin: '0 0 8px' }}>
                  Unlock AI Top Pick{savedPicks && savedPicks.length > 1 ? 's' : ''}
                </h2>
                {!alreadyPredicted && (
                  <p style={{ color: theme.textDark, fontSize: '17px', margin: 0 }}>
                    Make your prediction below to reveal today's AI top pick
                  </p>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'stretch', gap: '20px' }}>
                {/* Left: AI side — compact */}
                <div style={{ width: '260px', flexShrink: 0, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  {/* AI robot — always use original 3D robot */}
                  <img src="/robot1.png" style={{
                    width: 180,
                    height: 180,
                    marginBottom: '16px',
                    animation: 'float 3s ease-in-out infinite',
                  }} alt="AI" />
                  <div style={{
                    background: theme.bg,
                    border: '1px solid ' + theme.border,
                    borderRadius: '12px',
                    padding: '20px 24px',
                    width: '100%',
                    textAlign: 'left',
                  }}>
                    <p style={{ color: theme.teal, fontSize: '16px', fontWeight: 700, margin: '0 0 12px' }}>
                      AI View on {currentTicker}
                    </p>
                    {aiPrediction ? (
                      <>
                        <p style={{ color: theme.textDark, fontSize: '14px', margin: '0 0 8px' }}>
                          Direction: <span style={{ color: theme.text, fontWeight: 700, fontSize: '16px' }}>
                            {aiPrediction === 'big_rise' && '📈 Strong Rise'}
                            {aiPrediction === 'small_rise' && '↗ Small Rise'}
                            {aiPrediction === 'flat' && '➡ Flat'}
                            {aiPrediction === 'small_drop' && '↘ Small Drop'}
                            {aiPrediction === 'big_drop' && '📉 Strong Drop'}
                          </span>
                        </p>
                        <p style={{ color: theme.textDark, fontSize: '14px', margin: '0 0 8px' }}>
                          Confidence: <span style={{ color: aiConfidence >= 70 ? '#22a06b' : aiConfidence >= 50 ? '#e0aa3e' : '#cf4444', fontWeight: 700, fontSize: '16px' }}>{aiConfidence}%</span>
                        </p>
                        <p style={{ color: theme.textDark, fontSize: '14px', margin: '0 0 12px' }}>
                          Signal Strength: <span style={{
                            fontWeight: 700, fontSize: '14px', padding: '2px 10px', borderRadius: '4px',
                            background: aiStrength === 'Strong' ? 'rgba(34,160,107,0.12)' : aiStrength === 'Medium' ? 'rgba(224,170,62,0.12)' : 'rgba(207,68,68,0.12)',
                            color: aiStrength === 'Strong' ? '#22a06b' : aiStrength === 'Medium' ? '#e0aa3e' : '#cf4444',
                          }}>{aiStrength}</span>
                        </p>
                        <p style={{ fontSize: '14px', color: theme.text, margin: 0, fontWeight: 600 }}>Do you agree? What's your call?</p>
                      </>
                    ) : (
                      <p style={{ fontSize: '16px', color: theme.textMuted, margin: 0, fontStyle: 'italic' }}>Analyzing...</p>
                    )}
                  </div>
                </div>

                {/* Center: VS divider */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '0 4px' }}>
                  <div style={{ width: '1px', flex: 1, background: theme.border }} />
                  <p style={{ color: theme.teal, fontSize: '22px', fontWeight: 800, margin: '12px 0', letterSpacing: '2px' }}>VS</p>
                  <div style={{ width: '1px', flex: 1, background: theme.border }} />
                </div>

                {/* Right: DailyPrediction component — takes most space */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <DailyPrediction
                    ticker={currentTicker}
                    onSubmit={handlePredictionSubmit}
                    alreadyPredicted={alreadyPredicted}
                    existingPrediction={userPrediction ? { tier: userPrediction, mood: userMood } : null}
                    aiTier={aiPrediction}
                    savedPicks={savedPicks}
                    onSavePick={(picks) => {
                      const picksArray = Array.isArray(picks) ? picks : [picks];
                      setSavedPicks(picksArray);
                      if (user) saveTodayPick(user.uid, picksArray);
                    }}
                  />
                </div>
              </div>

              {/* Share Button - only after prediction saved */}
              {alreadyPredicted && userPrediction && (
                <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
                  <SharePrediction ticker={currentTicker} userTier={userPrediction} aiTier={aiPrediction} />
                </div>
              )}
            </div>

            {/* K-line Chart */}
            {klineData && klineData.length > 0 && (
              <div style={{ marginBottom: '28px' }}>
                <StockKlineChart
                  ticker={currentTicker}
                  priceHistory={klineData}
                  userPrediction={userPrediction}
                  aiPrediction={aiPrediction}
                />
              </div>
            )}

            {/* Collapsible Deep Analysis Section */}
            <div style={{ marginBottom: '28px' }}>
              {/* ===== TECHNICAL + SENTIMENT Side by Side ===== */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                {/* Technical Indicators */}
                <div style={{
                  background: theme.bgCard,
                  border: '1px solid ' + theme.border,
                  borderRadius: '14px',
                  overflow: 'hidden',
                }}>
                  <div onClick={() => setShowTechnical(!showTechnical)} style={{
                    padding: '20px 24px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    backgroundColor: showTechnical ? 'rgba(45, 212, 191, 0.05)' : 'transparent',
                    transition: 'background 0.2s'
                  }}>
                    <span style={{ fontSize: '18px', transition: 'transform 0.2s', transform: showTechnical ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
                    <h3 style={{ color: theme.text, fontSize: '18px', fontWeight: 600, margin: 0, flex: 1 }}>Technical Indicators</h3>
                  </div>
                  {showTechnical && (
                    <div style={{ padding: '20px 24px', borderTop: '1px solid ' + theme.border, background: theme.bg }}>
                      {data.technical ? (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                          <div>
                            <p style={{ color: theme.textDark, fontSize: '15px', margin: '0 0 8px' }}>MA Signal</p>
                            <SignalBadge signal={data.technical.maSignal} />
                          </div>
                          <div>
                            <p style={{ color: theme.textDark, fontSize: '15px', margin: '0 0 8px' }}>MACD Signal</p>
                            <SignalBadge signal={data.technical.macdSignal} />
                          </div>
                          <div>
                            <p style={{ color: theme.textDark, fontSize: '15px', margin: '0 0 8px' }}>Overall</p>
                            <SignalBadge signal={data.technical.overall} size="large" />
                          </div>
                          <div>
                            <p style={{ color: theme.textDark, fontSize: '15px', margin: '0 0 8px' }}>Volume</p>
                            <VolumeBadge signal={data.technical.volumeSignal} ratio={data.technical.volumeRatio} />
                          </div>
                          {(() => {
                            const mom = data.technical.momentum ?? clientIndicators.momentum;
                            if (mom === null || mom === undefined) return null;
                            const momColor = mom > 5 ? theme.green : mom < -5 ? theme.red : theme.neutral;
                            return (
                              <div>
                                <p style={{ color: theme.textDark, fontSize: '15px', margin: '0 0 8px' }}>Momentum (14D)</p>
                                <span style={{
                                  display: 'inline-flex', alignItems: 'center', gap: '8px',
                                  background: momColor + '33', border: '1px solid ' + momColor,
                                  borderRadius: '8px', padding: '10px 18px', fontSize: '17px', fontWeight: 700, color: momColor,
                                }}>
                                  <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: momColor }} />
                                  {mom > 0 ? '+' : ''}{mom.toFixed(1)}%
                                </span>
                              </div>
                            );
                          })()}
                          {(() => {
                            const rsi = data.technical.rsi ?? clientIndicators.rsi;
                            const sig = data.technical.rsiSignal ?? clientIndicators.rsiSignal;
                            if (rsi === null || rsi === undefined) return null;
                            const rsiColor = sig === 'overbought' ? theme.red : sig === 'oversold' ? theme.green : theme.neutral;
                            const rsiLabel = sig === 'overbought' ? 'Overbought' : sig === 'oversold' ? 'Oversold' : 'Neutral';
                            return (
                              <div>
                                <p style={{ color: theme.textDark, fontSize: '15px', margin: '0 0 8px' }}>RSI (14)</p>
                                <span style={{
                                  display: 'inline-flex', alignItems: 'center', gap: '8px',
                                  background: rsiColor + '33', border: '1px solid ' + rsiColor,
                                  borderRadius: '8px', padding: '10px 18px', fontSize: '17px', fontWeight: 700, color: rsiColor,
                                }}>
                                  <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: rsiColor }} />
                                  {rsi.toFixed(1)} ({rsiLabel})
                                </span>
                              </div>
                            );
                          })()}
                        </div>
                      ) : <p style={{ color: theme.textDark }}>No technical data</p>}
                    </div>
                  )}
                </div>

                {/* News Sentiment */}
                <div style={{
                  background: theme.bgCard,
                  border: '1px solid ' + theme.border,
                  borderRadius: '14px',
                  overflow: 'hidden',
                }}>
                  <div onClick={() => setShowSentiment(!showSentiment)} style={{
                    padding: '20px 24px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    backgroundColor: showSentiment ? 'rgba(45, 212, 191, 0.05)' : 'transparent',
                    transition: 'background 0.2s'
                  }}>
                    <span style={{ fontSize: '18px', transition: 'transform 0.2s', transform: showSentiment ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
                    <h3 style={{ color: theme.text, fontSize: '18px', fontWeight: 600, margin: 0, flex: 1 }}>News Sentiment</h3>
                  </div>
                  {showSentiment && (
                    <div style={{ padding: '20px 24px', borderTop: '1px solid ' + theme.border, background: theme.bg }}>
                      {data.sentiment ? (
                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <SentimentBadge sentiment={data.sentiment.overall} />
                            <span style={{ color: theme.textDark, fontSize: '16px' }}>{data.sentiment.total} articles</span>
                          </div>
                          <div style={{ display: 'flex', gap: '12px' }}>
                            <div style={{ flex: 1, textAlign: 'center', padding: '14px', background: 'rgba(63, 185, 80, 0.1)', borderRadius: '10px' }}>
                              <p style={{ color: theme.green, fontSize: '28px', fontWeight: 700, margin: 0 }}>{data.sentiment.positive}</p>
                              <p style={{ color: theme.textDark, fontSize: '14px', margin: '4px 0 0' }}>Positive</p>
                            </div>
                            <div style={{ flex: 1, textAlign: 'center', padding: '14px', background: 'rgba(139, 148, 158, 0.1)', borderRadius: '10px' }}>
                              <p style={{ color: theme.neutral, fontSize: '28px', fontWeight: 700, margin: 0 }}>{data.sentiment.neutral}</p>
                              <p style={{ color: theme.textDark, fontSize: '14px', margin: '4px 0 0' }}>Neutral</p>
                            </div>
                            <div style={{ flex: 1, textAlign: 'center', padding: '14px', background: 'rgba(248, 81, 73, 0.1)', borderRadius: '10px' }}>
                              <p style={{ color: theme.red, fontSize: '28px', fontWeight: 700, margin: 0 }}>{data.sentiment.negative}</p>
                              <p style={{ color: theme.textDark, fontSize: '14px', margin: '4px 0 0' }}>Negative</p>
                            </div>
                          </div>
                        </div>
                      ) : <p style={{ color: theme.textDark }}>No sentiment data</p>}
                    </div>
                  )}
                </div>
              </div>

              {/* ===== FINANCIAL CHARTS (Collapsible) ===== */}
              <div style={{
                background: theme.bgCard,
                border: '1px solid ' + theme.border,
                borderRadius: '14px',
                marginBottom: '16px',
                overflow: 'hidden',
              }}>
                <div onClick={() => setShowCharts(!showCharts)} style={{
                  padding: '20px 24px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  backgroundColor: showCharts ? 'rgba(45, 212, 191, 0.05)' : 'transparent',
                  transition: 'background 0.2s'
                }}>
                  <span style={{ fontSize: '20px', transition: 'transform 0.2s', transform: showCharts ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
                  <h3 style={{ color: theme.text, fontSize: '18px', fontWeight: 600, margin: 0, flex: 1 }}>Financial Charts</h3>
                  <span style={{ color: theme.textMuted, fontSize: '14px' }}>Revenue, Income, Cash Flow</span>
                </div>
                {showCharts && (
                  <div style={{ padding: '20px 24px', borderTop: '1px solid ' + theme.border, background: theme.bg }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                      <div>
                        <h4 style={{ color: theme.teal, fontSize: '16px', fontWeight: 600, margin: '0 0 16px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Revenue Trend</h4>
                        <ResponsiveContainer width="100%" height={240}>
                          <AreaChart data={data.financials}>
                            <defs><linearGradient id="rg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={theme.teal} stopOpacity={0.3} /><stop offset="100%" stopColor={theme.teal} stopOpacity={0} /></linearGradient></defs>
                            <CartesianGrid strokeDasharray="3 3" stroke={theme.border} />
                            <XAxis dataKey="period" tick={{ fill: theme.textDark, fontSize: 11 }} axisLine={{ stroke: theme.border }} />
                            <YAxis tick={{ fill: theme.textDark, fontSize: 11 }} axisLine={{ stroke: theme.border }} />
                            <Tooltip content={<CustomTooltip />} />
                            <Area type="monotone" dataKey="revenue" stroke={theme.teal} strokeWidth={2} fill="url(#rg)" name="Revenue" />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                      <div>
                        <h4 style={{ color: theme.purple, fontSize: '16px', fontWeight: 600, margin: '0 0 16px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Net Income</h4>
                        <ResponsiveContainer width="100%" height={240}>
                          <ComposedChart data={data.financials}>
                            <CartesianGrid strokeDasharray="3 3" stroke={theme.border} />
                            <XAxis dataKey="period" tick={{ fill: theme.textDark, fontSize: 11 }} axisLine={{ stroke: theme.border }} />
                            <YAxis tick={{ fill: theme.textDark, fontSize: 11 }} axisLine={{ stroke: theme.border }} />
                            <Tooltip content={<CustomTooltip />} />
                            <ReferenceLine y={0} stroke={theme.textDark} strokeDasharray="5 5" />
                            <Line type="monotone" dataKey="netIncome" stroke={theme.red} strokeWidth={2} dot={{ fill: theme.red, r: 4 }} name="Net Income" />
                          </ComposedChart>
                        </ResponsiveContainer>
                      </div>
                      <div>
                        <h4 style={{ color: '#3b82f6', fontSize: '16px', fontWeight: 600, margin: '0 0 16px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Cash Flow</h4>
                        <ResponsiveContainer width="100%" height={240}>
                          <BarChart data={data.financials}>
                            <CartesianGrid strokeDasharray="3 3" stroke={theme.border} />
                            <XAxis dataKey="period" tick={{ fill: theme.textDark, fontSize: 11 }} axisLine={{ stroke: theme.border }} />
                            <YAxis tick={{ fill: theme.textDark, fontSize: 11 }} axisLine={{ stroke: theme.border }} />
                            <Tooltip content={<CustomTooltip />} />
                            <ReferenceLine y={0} stroke={theme.textDark} strokeDasharray="5 5" />
                            <Bar dataKey="cashFlow" name="Cash Flow" radius={[4, 4, 0, 0]} fill="#3b82f6" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* ===== AI FUNDAMENTAL ANALYSIS (Collapsible) ===== */}
              <div style={{
                background: theme.bgCard,
                border: '1px solid ' + theme.border,
                borderRadius: '14px',
                overflow: 'hidden',
              }}>
                <div onClick={() => setShowAiAnalysis(!showAiAnalysis)} style={{
                  padding: '20px 24px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  backgroundColor: showAiAnalysis ? 'rgba(45, 212, 191, 0.05)' : 'transparent',
                  transition: 'background 0.2s'
                }}>
                  <span style={{ fontSize: '20px', transition: 'transform 0.2s', transform: showAiAnalysis ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
                  <h3 style={{ color: theme.text, fontSize: '18px', fontWeight: 600, margin: 0, flex: 1 }}>Deep Analysis</h3>
                  <span style={{ color: theme.textMuted, fontSize: '14px' }}>Risk • Market Context • Insights</span>
                </div>
                {showAiAnalysis && (
                  <div style={{ padding: '24px', borderTop: '1px solid ' + theme.border, background: '#0d1117' }}>
                    {/* 1. The Bottom Line */}
                    <div style={{ marginBottom: '24px' }}>
                      <h4 style={{ color: theme.teal, fontSize: '16px', fontWeight: 600, margin: '0 0 16px' }}>The Bottom Line</h4>
                      {data.analysis?.riskClassification ? (() => {
                        const rl = (data.analysis.riskClassification.riskLevel || '').toLowerCase();
                        const riskIcon = rl.includes('high') ? '🔴' : rl.includes('medium') ? '🟡' : rl.includes('low') ? '🟢' : '⚪';
                        const riskColor = rl.includes('high') ? theme.red : rl.includes('medium') ? theme.neutral : rl.includes('low') ? theme.green : theme.textMuted;
                        return (
                          <div style={{ display: 'flex', gap: '16px' }}>
                            <div style={{ background: riskColor + '15', border: '1px solid ' + riskColor + '50', borderRadius: '10px', padding: '16px', minWidth: '140px', textAlign: 'center' }}>
                              <div style={{ fontSize: '28px', marginBottom: '8px' }}>{riskIcon}</div>
                              <p style={{ color: riskColor, fontSize: '14px', fontWeight: 700, margin: '0 0 4px' }}>
                                {data.analysis.riskClassification.riskLevel}
                              </p>
                              <p style={{ color: theme.textDark, fontSize: '11px', margin: 0, textTransform: 'uppercase' }}>Risk</p>
                            </div>
                            <div style={{ flex: 1 }}>
                              <p style={{ color: theme.text, fontSize: '16px', lineHeight: '1.6', margin: '0 0 12px' }}>
                                <strong>{data.analysis.riskClassification.basis}</strong>
                              </p>
                              {data.judgeSummary && (
                                <p style={{ color: theme.textMuted, fontSize: '16px', lineHeight: '1.6', margin: 0 }}>
                                  {data.judgeSummary}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })() : (
                        <p style={{ color: theme.textDark, fontSize: '14px' }}>Loading...</p>
                      )}
                    </div>

                    {/* 2. Market Context */}
                    <div style={{ marginBottom: '24px' }}>
                      <h4 style={{ color: theme.teal, fontSize: '16px', fontWeight: 600, margin: '0 0 16px' }}>Market Context</h4>
                      {marketContextAnalysis?.bottomLine ? (
                        <p style={{ color: theme.text, fontSize: '16px', lineHeight: '1.6', margin: 0 }}>
                          {marketContextAnalysis.bottomLine}
                        </p>
                      ) : (
                        <p style={{ color: theme.textDark, fontSize: '14px' }}>
                          {marketContextLoading ? 'Analyzing market...' : 'Loading market context...'}
                        </p>
                      )}
                    </div>

                    {/* 3. Key Business Insights */}
                    <div>
                      <h4 style={{ color: theme.teal, fontSize: '16px', fontWeight: 600, margin: '0 0 16px' }}>Key Insights</h4>
                      {data.analysis ? (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                          <div>
                            <h5 style={{ color: theme.teal, fontSize: '13px', fontWeight: 600, margin: '0 0 12px', textTransform: 'uppercase' }}>Opportunities</h5>
                            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                              {(data.analysis.opportunities || []).slice(0, 2).map((item, j) => (
                                <li key={j} style={{ color: theme.text, fontSize: '14px', padding: '8px 0', lineHeight: '1.5', display: 'flex', gap: '8px' }}>
                                  <span style={{ color: theme.teal }}>▸</span> <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div>
                            <h5 style={{ color: theme.purple, fontSize: '13px', fontWeight: 600, margin: '0 0 12px', textTransform: 'uppercase' }}>Milestones</h5>
                            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                              {(data.analysis.milestones || []).slice(0, 2).map((item, j) => (
                                <li key={j} style={{ color: theme.text, fontSize: '14px', padding: '8px 0', lineHeight: '1.5', display: 'flex', gap: '8px' }}>
                                  <span style={{ color: theme.purple }}>▸</span> <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div>
                            <h5 style={{ color: theme.red, fontSize: '13px', fontWeight: 600, margin: '0 0 12px', textTransform: 'uppercase' }}>Risks</h5>
                            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                              {(data.analysis.risks || []).slice(0, 2).map((item, j) => (
                                <li key={j} style={{ color: theme.text, fontSize: '14px', padding: '8px 0', lineHeight: '1.5', display: 'flex', gap: '8px' }}>
                                  <span style={{ color: theme.red }}>▸</span> <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      ) : (
                        <p style={{ color: theme.textDark, fontSize: '14px' }}>Loading insights...</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Market Sense IQ */}
            <div style={{ marginBottom: '28px' }}>
              <MarketSenseIQ
                iqScore={iqScore}
                streak={streak}
                bestStreak={bestStreak}
                moodStats={moodStats}
                history={predictionHistory}
              />
            </div>

            {/* Settlement Modal */}
            <SettlementModal
              visible={showSettlement}
              onClose={() => setShowSettlement(false)}
              settlement={settlementData}
            />

            {/* Footer */}
            <footer style={{ marginTop: '36px', textAlign: 'center', paddingTop: '28px', borderTop: '1px solid ' + theme.border }}>
              <p style={{ color: theme.textDark, fontSize: '18px', margin: '0 0 10px' }}>Data provided by Polygon.io · Analysis by AI</p>
              <p style={{ color: theme.textDark, fontSize: '16px', margin: 0, fontStyle: 'italic' }}>For informational purposes only. Not financial advice.</p>
            </footer>

            <style>{`
              @keyframes spin { to { transform: rotate(360deg); } }
              @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.7; } }
              @keyframes float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
            `}</style>
          </>
        )}
      </div>
    </div>
  );
}

export default FullAnalysisPage;
